import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { LayoutGrid, LogOut, ShieldCheck } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";

export default function AppShell({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-home-link">
            <div className="h-8 w-8 rounded-lg bg-slate-900 flex items-center justify-center">
              <LayoutGrid className="h-4 w-4 text-white" />
            </div>
            <span className="font-heading font-semibold text-slate-900 text-lg tracking-tight">Collably</span>
          </Link>

          <div className="flex items-center gap-4">
            <NotificationBell />
            {isAdmin && (
              <span
                className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full"
                data-testid="admin-badge"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Admin
              </span>
            )}
            <div className="flex items-center gap-2" data-testid="current-user">
              <Avatar className="h-8 w-8">
                <AvatarFallback className={`text-xs font-semibold ${isAdmin ? "bg-amber-500 text-white" : "bg-indigo-600 text-white"}`} data-testid="user-initials">
                  {(user?.name || "U").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("")}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:block text-sm text-slate-600">{user?.name}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-500 hover:text-slate-900"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">{children}</main>
    </div>
  );
}
